import React, { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../api';

const BLUE = '#2B7FFF';
const SURFACE = '#0B1120';
const CARD = '#0F1929';
const OFF_WHITE = '#A0AABB';
const WHITE = '#FFFFFF';

const DEFAULT_PROFILE = {
  displayName: 'Christian G Flair',
  title: 'Master Professional',
  location: 'Royal Palm Beach, FL',
  address: '1017 N SR-7 Suite 122, Royal Palm Beach, FL',
  phone: '5618885567',
  bio: '',
  years: '8+',
  clientCount: '1,000+',
  reviewScore: '5.0',
  reviewCount: '148+',
  instagram: 'christiangflair',
  tiktok: '',
  facebook: '',
  youtube: '',
  accentColor: '#2B7FFF',
  ctaHeadline: 'Ready to get fresh?',
  ctaSubtext: 'Book with Christian. Card on file. Confirmed in minutes.',
  ctaButtonText: 'Book Your Appointment',
  hoursText: 'Mon–Sat 8AM–11PM · Sun 6PM–11PM',
  businessSlug: 'christiangflair',
  coverImage: '',
  tagline: '',
  portfolio: [],
};

const TABS = [
  { key: 'identity', label: 'Identity' },
  { key: 'stats', label: 'Stats & Reviews' },
  { key: 'social', label: 'Social' },
  { key: 'photos', label: 'Portfolio' },
  { key: 'theme', label: 'Theme' },
  { key: 'url', label: 'Your Link' },
];

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: OFF_WHITE, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</label>
      {hint && <p style={{ fontSize: '11px', color: '#445', marginBottom: '6px', margin: '0 0 6px' }}>{hint}</p>}
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', prefix }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', background: SURFACE, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
      {prefix && <span style={{ padding: '0 12px', color: '#445', fontSize: '13px', borderRight: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{prefix}</span>}
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ flex: 1, background: 'none', border: 'none', outline: 'none', padding: '12px', color: WHITE, fontSize: '14px' }}
      />
    </div>
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ width: '100%', background: SURFACE, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', outline: 'none', padding: '12px', color: WHITE, fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
    />
  );
}

export default function ProfileEditor() {
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [tab, setTab] = useState('identity');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const portfolioInputRef = useRef(null);

  useEffect(() => {
    api.get('/settings').then(r => {
      if (r.data.profile) {
        try { setProfile({ ...DEFAULT_PROFILE, ...JSON.parse(r.data.profile) }); }
        catch {}
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setProfile(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put('/settings/profile', { value: JSON.stringify(profile) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {} finally { setSaving(false); }
  };

  const handlePortfolioAdd = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 600 * 1024) return;
      const reader = new FileReader();
      reader.onload = ev => {
        setProfile(p => ({
          ...p,
          portfolio: [...(p.portfolio || []).slice(0, 7), { src: ev.target.result, caption: '', service: '' }],
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const updatePortfolioItem = (idx, key, val) => {
    setProfile(p => {
      const portfolio = [...(p.portfolio || [])];
      portfolio[idx] = { ...portfolio[idx], [key]: val };
      return { ...p, portfolio };
    });
  };

  const removePortfolioItem = (idx) => {
    setProfile(p => ({ ...p, portfolio: (p.portfolio || []).filter((_, i) => i !== idx) }));
  };

  const tabStyle = (active) => ({
    padding: '10px 16px', background: 'none', border: 'none',
    borderBottom: active ? `2px solid ${BLUE}` : '2px solid transparent',
    color: active ? BLUE : '#555', fontWeight: active ? 700 : 400,
    fontSize: '12px', letterSpacing: '0.06em', textTransform: 'uppercase',
    cursor: 'pointer', whiteSpace: 'nowrap',
  });

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#334' }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent' }}>
      <AdminSidebar />
      <main style={{ flex: 1, overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em', color: WHITE, margin: 0 }}>My Profile</h1>
            <p style={{ color: '#445', fontSize: '12px', marginTop: '4px' }}>
              Edit what clients see on <a href={`/${profile.businessSlug || 'christiangflair'}`} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, textDecoration: 'none' }}>joinmane.com/{profile.businessSlug || 'christiangflair'} ↗</a>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {saved && <span style={{ color: '#10b981', fontSize: '13px' }}>Saved!</span>}
            <a href="/barber" target="_blank" rel="noopener noreferrer" style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>
              Preview ↗
            </a>
            <button onClick={handleSave} disabled={saving} style={{ padding: '9px 20px', borderRadius: '8px', background: BLUE, color: WHITE, fontWeight: 700, fontSize: '13px', border: 'none', cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)', overflowX: 'auto', padding: '0 32px' }}>
          {TABS.map(t => <button key={t.key} style={tabStyle(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
        </div>

        {/* Content */}
        <div style={{ padding: 'clamp(24px,4vw,40px) 32px', maxWidth: '680px' }}>

          {tab === 'identity' && (
            <>
              <Field label="Display Name">
                <Input value={profile.displayName} onChange={v => set('displayName', v)} placeholder="Christian G Flair" />
              </Field>
              <Field label="Title / Specialty" hint="Shown under your name in the hero">
                <Input value={profile.title} onChange={v => set('title', v)} placeholder="Master Professional" />
              </Field>
              <Field label="About / Bio" hint="Tell clients who you are. Be authentic — 2-4 sentences works best.">
                <Textarea value={profile.bio} onChange={v => set('bio', v)} placeholder="8+ years bringing precision cuts and good vibes to Royal Palm Beach. Every client gets my full attention, every time." rows={5} />
              </Field>
              <Field label="Location Tag" hint="Appears in the verified badge under your name">
                <Input value={profile.location} onChange={v => set('location', v)} placeholder="Royal Palm Beach, FL" />
              </Field>
              <Field label="Full Address">
                <Input value={profile.address} onChange={v => set('address', v)} placeholder="1017 N SR-7 Suite 122, Royal Palm Beach, FL" />
              </Field>
              <Field label="Phone">
                <Input value={profile.phone} onChange={v => set('phone', v)} placeholder="5618885567" />
              </Field>
              <Field label="Hours">
                <Input value={profile.hoursText} onChange={v => set('hoursText', v)} placeholder="Mon–Sat 8AM–11PM · Sun 6PM–11PM" />
              </Field>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px', marginTop: '8px' }}>
                <h3 style={{ color: OFF_WHITE, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Bottom CTA</h3>
                <Field label="Headline">
                  <Input value={profile.ctaHeadline} onChange={v => set('ctaHeadline', v)} placeholder="Ready to get fresh?" />
                </Field>
                <Field label="Subtext">
                  <Input value={profile.ctaSubtext} onChange={v => set('ctaSubtext', v)} placeholder="Book with Christian. Card on file. Confirmed in minutes." />
                </Field>
              </div>
            </>
          )}

          {tab === 'stats' && (
            <>
              <p style={{ color: '#445', fontSize: '13px', marginBottom: '24px' }}>These numbers appear in your hero section and build trust with new clients.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Field label="Review Score">
                  <Input value={profile.reviewScore} onChange={v => set('reviewScore', v)} placeholder="5.0" />
                </Field>
                <Field label="Review Count">
                  <Input value={profile.reviewCount} onChange={v => set('reviewCount', v)} placeholder="148+" />
                </Field>
                <Field label="Years Experience">
                  <Input value={profile.years} onChange={v => set('years', v)} placeholder="8+" />
                </Field>
                <Field label="Clients Served">
                  <Input value={profile.clientCount} onChange={v => set('clientCount', v)} placeholder="1,000+" />
                </Field>
              </div>
            </>
          )}

          {tab === 'social' && (
            <>
              <p style={{ color: '#445', fontSize: '13px', marginBottom: '24px' }}>Social links appear in your profile footer and CTA section.</p>
              <Field label="Instagram">
                <Input value={profile.instagram} onChange={v => set('instagram', v)} placeholder="christiangflair" prefix="instagram.com/" />
              </Field>
              <Field label="TikTok">
                <Input value={profile.tiktok} onChange={v => set('tiktok', v)} placeholder="username" prefix="tiktok.com/@" />
              </Field>
              <Field label="YouTube">
                <Input value={profile.youtube} onChange={v => set('youtube', v)} placeholder="@channelname" prefix="youtube.com/" />
              </Field>
              <Field label="Facebook">
                <Input value={profile.facebook} onChange={v => set('facebook', v)} placeholder="Page URL" prefix="facebook.com/" />
              </Field>
            </>
          )}

          {tab === 'photos' && (
            <>
              <p style={{ color: '#445', fontSize: '13px', marginBottom: '8px' }}>
                Add up to 8 portfolio photos. Tag each one to a service so clients see your work before they book.
              </p>
              <p style={{ color: '#334', fontSize: '11px', marginBottom: '24px' }}>PNG or JPG · max 600KB per photo</p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {(profile.portfolio || []).map((item, idx) => (
                  <div key={idx} style={{ background: CARD, borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ position: 'relative', height: '140px', background: SURFACE }}>
                      <img src={item.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button onClick={() => removePortfolioItem(idx)} style={{
                        position: 'absolute', top: '6px', right: '6px',
                        background: 'rgba(0,0,0,0.7)', border: 'none', color: '#ef4444',
                        borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer',
                        fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>×</button>
                    </div>
                    <div style={{ padding: '8px' }}>
                      <input
                        value={item.caption || ''}
                        onChange={e => updatePortfolioItem(idx, 'caption', e.target.value)}
                        placeholder="Caption..."
                        style={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: OFF_WHITE, fontSize: '11px', marginBottom: '4px', boxSizing: 'border-box' }}
                      />
                      <input
                        value={item.service || ''}
                        onChange={e => updatePortfolioItem(idx, 'service', e.target.value)}
                        placeholder="Service tag..."
                        style={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: '#445', fontSize: '10px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                ))}

                {(profile.portfolio || []).length < 8 && (
                  <button onClick={() => portfolioInputRef.current?.click()} style={{
                    height: '180px', background: SURFACE, border: '1px dashed rgba(255,255,255,0.1)',
                    borderRadius: '10px', cursor: 'pointer', color: '#334', fontSize: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>+</button>
                )}
              </div>
              <input ref={portfolioInputRef} type="file" accept="image/*" multiple onChange={handlePortfolioAdd} style={{ display: 'none' }} />
            </>
          )}

          {tab === 'theme' && (
            <>
              <p style={{ color: '#445', fontSize: '13px', marginBottom: '24px' }}>Choose your accent color — used for buttons, highlights, and your name on the profile page.</p>
              <Field label="Accent Color">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="color"
                    value={profile.accentColor || '#2B7FFF'}
                    onChange={e => set('accentColor', e.target.value)}
                    style={{ width: '48px', height: '48px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', padding: '2px', background: SURFACE }}
                  />
                  <Input value={profile.accentColor} onChange={v => set('accentColor', v)} placeholder="#2B7FFF" />
                </div>
              </Field>

              <div style={{ marginTop: '24px' }}>
                <p style={{ color: '#445', fontSize: '12px', marginBottom: '12px' }}>Quick presets:</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Mane Blue', color: '#2B7FFF' },
                    { label: 'Gold', color: '#C9A84C' },
                    { label: 'Champagne', color: '#E8D5A3' },
                    { label: 'Rose Gold', color: '#B76E79' },
                    { label: 'Emerald', color: '#10b981' },
                    { label: 'Purple', color: '#8b5cf6' },
                    { label: 'Crimson', color: '#DC143C' },
                    { label: 'Midnight', color: '#7C83FD' },
                    { label: 'White', color: '#e5e7eb' },
                    { label: 'Slate', color: '#94a3b8' },
                  ].map(({ label, color }) => (
                    <button key={color} onClick={() => set('accentColor', color)} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 14px', borderRadius: '8px', border: `1px solid ${profile.accentColor === color ? color : 'rgba(255,255,255,0.08)'}`,
                      background: profile.accentColor === color ? 'rgba(255,255,255,0.06)' : 'none',
                      cursor: 'pointer', color: WHITE, fontSize: '12px',
                    }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '28px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px' }}>
                <h3 style={{ color: OFF_WHITE, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Book Button Text</h3>
                <Field label="CTA Button Label" hint="What shows on the main booking button">
                  <Input value={profile.ctaButtonText} onChange={v => set('ctaButtonText', v)} placeholder="Book Your Appointment" />
                </Field>
              </div>

              <div style={{ marginTop: '28px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px' }}>
                <h3 style={{ color: OFF_WHITE, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Tagline</h3>
                <Field label="Short Tagline" hint="One line shown under your name. Max 60 characters.">
                  <Input value={profile.tagline} onChange={v => set('tagline', v)} placeholder="Precision cuts. Every time." />
                </Field>
              </div>
            </>
          )}

          {tab === 'url' && (
            <>
              <p style={{ color: '#445', fontSize: '13px', marginBottom: '24px' }}>
                Your public profile URL. Use your business name — no spaces, no special characters.
              </p>
              <Field label="Your Link" hint="This is the link you share with clients">
                <Input value={profile.businessSlug} onChange={v => set('businessSlug', v.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/\s+/g, '-'))} prefix="joinmane.com/" placeholder="christiangflair" />
              </Field>
              {profile.businessSlug && (
                <div style={{ marginTop: '8px', padding: '12px 16px', background: 'rgba(43,127,255,0.06)', border: '1px solid rgba(43,127,255,0.15)', borderRadius: '8px' }}>
                  <p style={{ color: '#2B7FFF', fontSize: '13px', fontWeight: 600 }}>
                    joinmane.com/{profile.businessSlug}
                  </p>
                  <p style={{ color: '#445', fontSize: '11px', marginTop: '4px' }}>Save changes and this becomes your shareable link</p>
                </div>
              )}
              <div style={{ marginTop: '28px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px' }}>
                <h3 style={{ color: OFF_WHITE, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Cover Image URL</h3>
                <Field label="Cover / Banner Image" hint="Paste a direct image URL — shown as your hero background">
                  <Input value={profile.coverImage} onChange={v => set('coverImage', v)} placeholder="https://..." />
                </Field>
                {profile.coverImage && (
                  <img src={profile.coverImage} alt="Cover preview" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px', border: '1px solid rgba(255,255,255,0.06)' }} />
                )}
              </div>
            </>
          )}

        </div>
      </main>
    </div>
  );
}
